"use client";
import { AppInput } from "./AppInput";
import { AppDatePicker } from "./AppDatePicker";
import { AppTime } from "./AppTime";
import { AppSelect } from "./AppSelect";
import { AppAutoComplete } from "./AppAutoComplete";
import { AppGrid } from "./AppGrid";
import { AppModal } from "./AppModal";
import AppNumber from "./AppNumber";
import AppForm from "./AppForm";
import { AppTextArea } from "./AppTextArea";
import { Form } from "antd";
import { toPascalCase } from "@/lib/format";
import { ComponentType } from "react";
import dayjs from "dayjs";
import { AppHInput } from "./AppHInput";

interface FormItemProps {
  label?: string;
  name?: string;
  required?: boolean;
  error?: string;
  // Span every column when the form is laid out as a grid. Has to sit on the
  // Form.Item, not the input — the Form.Item is the grid child.
  fullWidth?: boolean;
  [key: string]: any;
  target?: string[];
}


// Higher-order function to wrap components with Form.Item
function withFormItem<P extends FormItemProps>(
  Component: ComponentType<Omit<P, keyof FormItemProps>>,
  isDate: boolean = false
): ComponentType<P> {
  return function WrappedComponent({
    label,
    name,
    required,
    error,
    target,
    fullWidth,
    onChange,
    initialValue,
    ...props
  }: P) {

    const displayLabel = toPascalCase(label || name || '')

    // Form.Item's `required` only draws the asterisk — validation comes from
    // `rules`. Without this a field marked required submitted empty, and the
    // failure surfaced as a raw database error from the API instead.
    const rules = required
      ? [{ required: true, message: `${displayLabel} is required` }]
      : undefined;

    // Wrap onChange to handle both Form.Item's onChange and custom onChange handlers
    const handleChange = (value: any, ...args: any[]) => {
      // Call custom onChange first
      if (onChange) {
        onChange(value, ...args);
      }
    };

    return (
      <Form.Item
        label={displayLabel}
        initialValue={isDate ? dayjs(initialValue) : initialValue}
        name={name}
        required={required}
        rules={rules}
        style={fullWidth ? { gridColumn: "1 / -1" } : undefined}
        validateStatus={error ? "error" : undefined}
        help={error}
      >
        <Component {...(props as any)} onChange={handleChange} />
      </Form.Item>
    );
  };
}

// Wrapper for hidden fields - no label
function withFormItemHidden<P extends FormItemProps>(
  Component: ComponentType<Omit<P, keyof FormItemProps>>
): ComponentType<P> {
  return function WrappedComponent({
    label,
    name,
    required,
    error,
    target,
    onChange,
    initialValue,
    ...props
  }: P) {
    
    // Wrap onChange to handle both Form.Item's onChange and custom onChange handlers
    const handleChange = (value: any, ...args: any[]) => {
      // Call custom onChange first
      if (onChange) {
        onChange(value, ...args);
      }
    };

    return (
      <Form.Item
        label={undefined}
        initialValue={initialValue}
        name={name}
        required={required}
        validateStatus={error ? "error" : undefined}
        help={error}
      >
        <Component {...(props as any)} onChange={handleChange} />
      </Form.Item>
    );
  };
}


// Wrapped versions (with Form.Item)
const TextWithForm = withFormItem(AppInput);
const DateWithForm = withFormItem(AppDatePicker, true);
const TimeWithForm = withFormItem(AppTime, true);
const SelectWithForm = withFormItem(AppSelect);
const AutoCompleteWithForm = withFormItem(AppAutoComplete);
const NumberWithForm = withFormItem(AppNumber);
const TextAreaWithForm = withFormItem(AppTextArea);
const HiddenWithForm = withFormItemHidden(AppHInput);
const Facet = {
  // With Form.Item (default)
  Text: TextWithForm,
  Date: DateWithForm,
  Time: TimeWithForm,
  Select: SelectWithForm,
  AutoComplete: AutoCompleteWithForm,
  Number: NumberWithForm,
  TextArea: TextAreaWithForm,
  Hidden: HiddenWithForm,

  // Without Form.Item (N prefix = No form)
  NText: AppInput,
  NDate: AppDatePicker,
  NTime: AppTime,
  NSelect: AppSelect,
  NAutoComplete: AppAutoComplete,
  NNumber: AppNumber,
  NTextArea: AppTextArea,
  NHidden: AppHInput,

  // Form and Grid
  Form: AppForm,
  Grid: AppGrid,
  Modal: AppModal,
};

export default Facet;
